import { canonicalUrl, safeJson } from "../../../shared/src/seo";
import { eagerImageSizes, type PublicImage } from "../../../shared/src/resized-images";
import { websiteStructuredData, type StructuredData } from "../../src/lib/structured-data";
import { isPlatformShell } from "../platform/shell";
import { isTemplatePreviewShell } from "./template-preview-shell";
import type { SiteShell } from "../server/shell";

export type PageHead = {
  /** The path being described; Home's head describes the whole site. */
  pathname: string;
  title: string;
  description: string;
  canonicalPath?: string;
  /** An address elsewhere that the author named as this page's original. */
  canonicalOverride?: string;
  noindex?: boolean;
  /** Home without an authored page uses the site's own SEO title and description. */
  homepageSeo?: boolean;
  /** A page with a picture of its own (a product) shares that instead of the cover. */
  image?: string;
  article?: boolean;
  structuredData?: StructuredData | StructuredData[];
  /** The picture the visitor most likely sees first, fetched before the page's content. */
  preloadImage?: PublicImage | null;
};

/**
 * A creator page's title, description, search and sharing tags. The
 * site's own SEO settings, password protection and previews decide whether search engines may
 * index it; a page that can't be indexed carries no structured data.
 */
export function creatorPageHead(shell: SiteShell | undefined, page: PageHead) {
  if (!shell) return {};
  const homepage = page.pathname === "/" && page.homepageSeo;
  const siteDescription = shell.seo.description || shell.creator.description.trim().slice(0, 320);
  const title = homepage ? shell.seo.title || shell.creator.name : page.title;
  const description = homepage ? siteDescription : page.description;
  const canonical =
    page.canonicalOverride || canonicalUrl(shell.origin, page.canonicalPath ?? page.pathname);
  const describable =
    !page.noindex && !shell.protectedSite && shell.seo.indexingEnabled && !shell.preview;
  const indexable = describable && shell.production;
  const sharing = page.image || shell.creator.coverUrl || shell.creator.logoUrl;
  const image = sharing ? new URL(sharing, shell.origin).href : undefined;
  const structured = describable
    ? [
        page.pathname === "/"
          ? websiteStructuredData(
              shell.origin,
              shell.creator.name,
              siteDescription,
              shell.creator.socials,
            )
          : null,
        ...[page.structuredData ?? []].flat(),
      ].filter((item): item is StructuredData => !!item)
    : [];
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { name: "robots", content: indexable ? "index,follow" : "noindex,nofollow" },
      { property: "og:type", content: page.article ? "article" : "website" },
      { property: "og:site_name", content: shell.creator.name },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: canonical },
      ...(image ? [{ property: "og:image", content: image }] : []),
      { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      ...(image ? [{ name: "twitter:image", content: image }] : []),
    ],
    links: [
      { rel: "canonical", href: canonical },
      ...(page.preloadImage
        ? [
            {
              rel: "preload",
              as: "image",
              href: page.preloadImage.src,
              imageSrcSet: page.preloadImage.srcset,
              imageSizes: page.preloadImage.srcset ? eagerImageSizes : undefined,
              fetchPriority: "high" as const,
            },
          ]
        : []),
    ],
    scripts: structured.map((item) => ({ type: "application/ld+json", children: safeJson(item) })),
  };
}

/** A route's head with its own stylesheet after the page's links. */
export function withStylesheet<T extends { links?: object[] }>(head: T, href: string) {
  return { ...head, links: [...(head.links ?? []), { rel: "stylesheet", href }] };
}

/** The creator site's shell, as the root route loaded it, from inside any route's head. */
export function shellFrom(matches: readonly { loaderData?: unknown; routeId: string }[]) {
  const shell = matches.find((match) => match.routeId === "__root__")?.loaderData;
  // Drate's own pages have a platform shell; creator heads describe nothing there.
  return shell && !isPlatformShell(shell) && !isTemplatePreviewShell(shell)
    ? (shell as SiteShell)
    : undefined;
}
